import {
  Box, Card, CardContent, CardHeader, Divider, Stack, TextField,
  Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Pagination, MenuItem,
  Select, InputLabel, FormControl, TableSortLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useState } from 'react';
import api from '@src/api/clients';
import ClientDropdown from '@src/components/ClientDropdown';

export default function BackendDataListPage() {
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState('');
  const [orderDirection, setOrderDirection] = useState('asc');

  const [filters, setFilters] = useState({
    phone: '',
    email: '',
    zip_code: '',
    client: '',
    issue: '',
    demo: '',
    has_sold: '',
    has_net_amount: '',
    status: '',
    territory: ''
  });

  const pageSize = 100;

  const fetchBackendData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        ordering: orderBy ? `${orderDirection === 'desc' ? '-' : ''}${orderBy}` : '',
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '')),
      }).toString();

      const res = await api.request({ url: `dashboard/backend-data/?${params}`, method: 'GET' });
      setRecords(res.data.results);
      setTotalPages(Math.ceil(res.data.count / pageSize));
    } catch (err) {
      console.error('Error fetching backend data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBackendData();
  }, [page, orderBy, orderDirection]);

  const handleSearch = () => {
    setPage(1);
    fetchBackendData();
  };

  const handleInputChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrderDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(field);
      setOrderDirection('asc');
    }
  };

  const toEST = (utc) =>
    utc ? new Date(utc).toLocaleString('en-US', { timeZone: 'America/New_York' }) : '-';

  const renderBooleanDropdown = (label, field) => (
    <FormControl size="small" sx={{ minWidth: 140 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={filters[field]}
        onChange={(e) => handleInputChange(field, e.target.value)}
        label={label}
      >
        <MenuItem value=""><em>Any</em></MenuItem>
        <MenuItem value="true">Yes</MenuItem>
        <MenuItem value="false">No</MenuItem>
      </Select>
    </FormControl>
  );

  const renderSortableHeader = (label, field) => (
    <TableCell sortDirection={orderBy === field ? orderDirection : false}>
      <TableSortLabel
        active={orderBy === field}
        direction={orderBy === field ? orderDirection : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box p={3}>
      <Card>
        <CardHeader title="Backend Data Records" />
        <Divider />
        <CardContent>
          <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" alignItems="center">
            <TextField label="Phone" value={filters.phone} onChange={(e) => handleInputChange('phone', e.target.value)} size="small" sx={{ minWidth: 180 }} />
            <TextField label="Email" value={filters.email} onChange={(e) => handleInputChange('email', e.target.value)} size="small" sx={{ minWidth: 180 }} />
            <TextField label="Zip Code" value={filters.zip_code} onChange={(e) => handleInputChange('zip_code', e.target.value)} size="small" sx={{ minWidth: 140 }} />
            <TextField label="Territory" value={filters.territory} onChange={(e) => handleInputChange('territory', e.target.value)} size="small" sx={{ minWidth: 140 }} />
            <TextField label="Status" value={filters.status} onChange={(e) => handleInputChange('status', e.target.value)} size="small" sx={{ minWidth: 140 }} />
            <Box sx={{ minWidth: 200 }}>
              <ClientDropdown value={filters.client} onChange={(value) => handleInputChange('client', value)} />
            </Box>
            {renderBooleanDropdown('Issue', 'issue')}
            {renderBooleanDropdown('Demo', 'demo')}
            {renderBooleanDropdown('Gross Sold', 'has_sold')}
            {renderBooleanDropdown('Net $', 'has_net_amount')}
            <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch} sx={{ height: 40 }}>
              Search
            </Button>
          </Stack>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {renderSortableHeader('Client', 'client__name')}
                  {renderSortableHeader('Refresh Date', 'updated_date')}
                  {renderSortableHeader('Created Date', 'created_date')}
                  {renderSortableHeader('Appointment Date', 'appointment_date')}
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Zip Code</TableCell>
                  <TableCell>Territory</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Issue</TableCell>
                  <TableCell>Demo</TableCell>
                  {renderSortableHeader('Sold Date', 'sold_date')}
                  {renderSortableHeader('Gross Amount', 'gross_amount')}
                  {renderSortableHeader('Net Amount', 'net_amount')}
                  <TableCell>First Lead Name</TableCell>
                  <TableCell>Lead Campaign</TableCell>
                  <TableCell>Lead Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((record) => {
                  const lead = record.related_leads?.[0];
                  return (
                    <TableRow key={record.unique_id}>
                      <TableCell>{record.client_name || '-'}</TableCell>
                      <TableCell>{toEST(record.updated_date)}</TableCell>
                      <TableCell>{record.created_date}</TableCell>
                      <TableCell>{record.appointment_date}</TableCell>
                      <TableCell>{record.phone || '-'}</TableCell>
                      <TableCell>{record.email || '-'}</TableCell>
                      <TableCell>{record.zip_code || '-'}</TableCell>
                      <TableCell>{record.territory || '-'}</TableCell>
                      <TableCell>{record.status || '-'}</TableCell>
                      <TableCell>{record.issue ? '✅' : '❌'}</TableCell>
                      <TableCell>{record.demo ? '✅' : '❌'}</TableCell>
                      <TableCell>{record.sold_date}</TableCell>
                      <TableCell>{record.gross_amount ? `$${record.gross_amount}` : '-'}</TableCell>
                      <TableCell>{record.net_amount ? `$${record.net_amount}` : '-'}</TableCell>
                      <TableCell>{lead ? `${lead.first_name || ''} ${lead.last_name || ''}` : '-'}</TableCell>
                      <TableCell>{lead?.campaign_name || '-'}</TableCell>
                      <TableCell>{lead?.source || '-'}</TableCell>
                    </TableRow>
                  );
                })}
                {records.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={16} align="center">
                      {loading ? 'Loading...' : 'No backend records found.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" justifyContent="flex-end" mt={2}>
            <Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} color="primary" shape="rounded" />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
